<template>
  <header>
    <div id="s"
         class="flex flex-col md:items-start xl:items-center xl:flex-row w-full pt-8 lg:pt-10 items-center gap-5 text-center md:text-left">
      <div class="pl-5">
        <p class="text-deep-indigo-pale text-sm mb-2 relative">
          <Icon id="star"
                class="md:absolute md:top-1/2 md:-translate-y-1/2 md:-left-5 mr-2 md:mr-0 text-lilac-base text-xs"/>
          <span>{{ $t('organization.help.how_to') }}</span>
        </p>
        <p>{{ $t('organization.help.ideas') }}</p>
      </div>
      <div class="flex gap-5 xl:contents w-full">
        <form @submit.prevent="submit"
              class="grow relative lg:ml-auto w-full lg:w-auto flex flex-col md:grid md:grid-cols-[1fr_max-content] gap-2.5 overflow-hidden md:bg-lilac-strong md:rounded-full">
          <label for="search" class="sr-only">{{ $t('organization.index.placeholder') }}</label>
          <input id="search" name="search" type="text" :placeholder="$t('organization.index.placeholder')"
                 :disabled="form.submitted" v-model="form.fields.question.value"
                 :class="['w-full p-4 pr-0 relative placeholder-lilac-grey disabled:brightness-50 disabled:cursor-not-allowed bg-lilac-strong md:bg-transparent rounded-full md:rounded-none outline-none', {'pr-10': $organization.search && !form.submitted}]">
          <div class="relative">
            <Transition name="fade">
              <button v-if="$organization.search && !form.submitted"
                      class="absolute left-0 top-1/2 -translate-x-[125%] -translate-y-1/2 bg-deep-indigo-heavy hover:brightness-125 disabled:hover:brightness-100 p-3 rounded-full text-[10px] aspect-square transition-[filter]"
                      @click.prevent.stop="reset" type="button">
                <Icon id="close"/>
              </button>
            </Transition>
            <IconButton type="submit" icon="star" :text="$t('miria.ask')"
                        class="w-full md:w-max md:h-full md:max-h-full"
                        :disabled="form.submitted || !valid" small-icon/>
          </div>
        </form>
        <div class="hidden lg:grid lg:grid-cols-2 lg:w-32 lg:h-12 rounded-full bg-deep-indigo-dark relative">
          <button class="relative z-10" @click.stop="$emit('closeGrid')" type="button">
            <Icon id="list"/>
          </button>
          <button class="relative z-10" @click.stop="$emit('openGrid')" type="button">
            <Icon id="grid"/>
          </button>
          <span
              :class="['bg-deep-indigo-heavy absolute top-0 left-0 rounded-full w-1/2 h-full transition-[left]', {'left-1/2': grid}]"></span>
        </div>
      </div>
    </div>
    <Transition name="fade" mode="out-in">
      <ContentBox v-if="form.submitted || $organization.search" class="mt-10 relative" id="r">
        <div
            :class="['flex flex-col justify-between lg:flex-row gap-6 lg:gap-14 pt-5 md:pt-0', {'items-center': !$organization.search}]">
          <div class="grow">
            <p class="text-base">
              <RawHtml :html="searchResponse" tag="span"/>
              <Transition name="fade">
                <WaitingDots v-if="!$organization.search"/>
              </Transition>
            </p>
            <div v-if="$organization.search?.suggestions"
                 class="mt-5 flex flex-col c border-b border-deep-indigo-stronger">
              <button v-for="(suggestion, suggestionIndex) in $organization.search?.suggestions"
                      @click.stop="ask(suggestion[$i18n.locale])"
                      :key="`suggestion-${suggestionIndex}`"
                      class="text-left text-lg py-4 border-t border-deep-indigo-stronger flex gap-2.5 justify-between group">
                <span class="group-hover:opacity-[0.85] transition-opacity">{{ suggestion[$i18n.locale] }}</span>
                <Icon id="arrow"
                      class="shrink-0 text-[14px] mt-1.5 text-deep-indigo-pale group-hover:translate-x-1 transition-transform"/>
              </button>
            </div>
          </div>
          <Transition name="fade" mode="out-in">
            <div v-if="!$organization.search" class="loader"/>
            <div v-else-if="$organization.search" class="flex flex-col justify-between">
              <IconButton :text="$t('share')" icon="clipboard"
                          class="absolute -top-2 right-5 md:relative md:top-auto md:right-auto md:mb-6" reverse
                          secondary
                          @click.stop="share"/>
              <div class="flex md:flex-col gap-4 justify-between">
                <p class="description text-sm">{{ $t('labels.useful_response') }}</p>
                <div class="flex rounded-lg border border-lilac-medium w-max max-w-full">
                  <button :class="[LIKE_BUTTON_BASE_CLASSES, {'!p-0 !w-0': liked === false}]" @click.stop="like"
                          :disabled="liked">
                    <Icon id="like"
                          :class="[LIKE_BUTTON_ICON_CLASSES, {'text-lilac-base': liked, 'opacity-0': liked === false}]"/>
                  </button>
                  <span v-if="liked === null" class="my-2 w-[1px] h-[calc(100%-1rem)] bg-lilac-medium"/>
                  <button :class="[LIKE_BUTTON_BASE_CLASSES, {'!p-0 !w-0': liked}]" :disabled="liked === false"
                          @click.stop="dislike">
                    <Icon id="like"
                          :class="[LIKE_BUTTON_ICON_CLASSES, 'rotate-180', {'text-lilac-base': liked === false, 'opacity-0': liked}]"/>
                  </button>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </ContentBox>
    </Transition>
  </header>
</template>

<script>
import {marked} from 'marked'
import {ContentBox} from '@/components/blocks'
import {Icon, RawHtml} from '@/components/helpers'
import {IconButton, WaitingDots} from '@/components/small'
import {FormValidMixin} from '@/components/form/mixins'
import {shuffle} from '@/helpers'

export default {
  name: 'OrganizationSearch',
  mixins: [FormValidMixin],
  setup() {
    return {
      LIKE_BUTTON_BASE_CLASSES: 'p-3.5 group transition-[padding_width] overflow-hidden duration-[400ms] disabled:cursor-not-allowed',
      LIKE_BUTTON_ICON_CLASSES: 'text-deep-indigo-pale group-hover:text-lilac-base transition-[color_opacity]'
    }
  },
  components: {RawHtml, ContentBox, Icon, IconButton, WaitingDots},
  props: {
    grid: {
      type: Boolean,
      required: true
    },
    modelValue: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      form: {
        fields: {
          question: {
            valid: false,
            value: ''
          }
        },
        submitted: false
      },
      liked: null
    }
  },
  computed: {
    organizations() {
      if (this.form.submitted) {
        return []
      }

      let organizations = Object.values(this.$app.organizations)

      if (this.$organization.search) {
        organizations = this.$organization.search.organizations
            .map(id => this.$app.organizations[id])
            .filter(org => org)
      } else {
        organizations = shuffle(organizations)
      }

      return organizations
    },
    searchResponse() {
      if (!this.$organization.search?.response?.[this.$i18n.locale]) {
        return this.$t('labels.searching')
      }

      return marked(this.$organization.search.response[this.$i18n.locale]).toString()
    }
  },
  methods: {
    async ask(question) {
      this.$organization.resetSearch()
      this.form.submitted = true

      this.form.fields.question.value = question
      this.liked = null

      this.$router.push({...this.$route, hash: '#s', query: null})

      await this.$organization.ask(question)

      this.form.submitted = false
    },
    dislike() {
      this.$organization.rateSearch(false)
      this.liked = false
      this.$modal.openOrganizationSearchFeedback()
    },
    like() {
      this.$organization.rateSearch()
      this.liked = true
    },
    reset() {
      this.form.fields.question = {valid: false, value: ''}
      this.$organization.resetSearch()
      this.$router.push({...this.$route, query: {}, hash: null})
    },
    share() {
      try {
        if (navigator.share) {
          const metaDescription = document.querySelector('meta[name="description"]')

          navigator.share({
            text: metaDescription ? metaDescription.getAttribute('content') : null,
            title: document.title,
            url: window.location.href
          })
        } else {
          navigator.clipboard.writeText(this.url)
        }
      } catch (e) {
      }
    },
    submit() {
      this.ask(this.form.fields.question.value)
    }
  },
  emits: ['closeGrid', 'openGrid', 'update:modelValue'],
  watch: {
    'form.fields.question.value'(value) {
      this.form.fields.question.valid = this.$regex.INSTRUCTIONS.test(value)
    },
    'organizations': {
      handler() {
        this.$emit('update:modelValue', this.organizations)
      },
      immediate: true
    },
    '$organization.search': {
      handler() {
        if (this.$organization.search) {
          const searchId = this.$organization.search.id.toString()

          if (!this.$route.query.s || this.$route.query.s !== searchId) {
            this.$nextTick(async () => {
              await this.$router.push({...this.$route, query: {s: searchId}, hash: '#r'})
            })
          }

          if (!this.form.fields.question.value) {
            this.form.fields.question.value = this.$organization.search.question
          }
        }
      },
      immediate: true
    },
    '$route.query.s': {
      async handler() {
        if (this.$route.query.s && this.$organization?.search?.id.toString() !== this.$route.query.s) {
          await this.$organization.getSearch(this.$route.query.s)
          this.form.fields.question.value = this.$organization.search.question

          if (this.$organization.search.useful !== null) {
            this.liked = this.$organization.search.useful === 1
          }

          await this.$nextTick(() => this.$router.push({...this.$route, hash: '#r'}))
        }
      },
      immediate: true
    }
  }
}
</script>